// DesignRebindSection — design-release rebinding UI for an instantiated
// template project (Plan B3 Task 4, cogo.template.v1 content-service loop,
// spec §3.4/§6.5's SECOND variation axis: content stays fixed, the design
// RELEASE swaps). Sibling of `TemplateContentPanel`'s content-slot loop
// (Plan B2 Task 4) — mounted by it, not standalone.
//
// Hard rules (mirrors TemplateContentPanel's own):
//  - only ever rendered/offered when `entry.pkg.designBinding.rebinding ===
//    'allowed'` — `TemplateContentPanel` gates mounting this component on
//    that condition, and this component re-checks it itself
//    (belt-and-braces, same double-guard idiom `products-records`'
//    `readOnly` field uses elsewhere in this loop) so it's safe even if a
//    future caller mounts it unconditionally.
//  - applying a preset calls `applyDesignRebind`, which is itself
//    fail-closed (a persist rejection touches neither store) — failures are
//    shown inline, never silently dropped.
//  - PRESENTATION-only: this section only ever offers a design TOKENS swap,
//    never a content edit — it has no path to any block's ui.json/data.json.

import { useEffect, useState } from 'react'
import type { TemplateRegistryEntry } from '../registry'
import { fetchDesignSystemPresets, type DesignSystemPreset } from '@/features/design-system/lib/fetch-presets'
import { getStudioUserId } from '@/services/auth-provider'
import { toast } from '@/store/toast-store'
import { useStudioLocale } from '@/i18n'
import { applyDesignRebind } from './apply-design-rebind'

export interface DesignRebindSectionProps {
  entry: TemplateRegistryEntry
}

export function DesignRebindSection({ entry }: DesignRebindSectionProps) {
  // Same hook/prop path `TemplateContentPanel` (this section's mount point)
  // already uses for its own copy — see that component's `strings.templateContent`
  // read at its top. Keeping this section's copy in the SAME dictionary
  // object (rather than a sibling one) matches it being the sibling UI
  // TemplateContentPanel mounts, per this file's header comment.
  const { strings } = useStudioLocale()
  const copy = strings.templateContent
  const [presets, setPresets] = useState<DesignSystemPreset[]>([])
  const [presetsError, setPresetsError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [applyStatus, setApplyStatus] = useState<'idle' | 'loading'>('idle')
  const [applyError, setApplyError] = useState<string | null>(null)

  useEffect(() => {
    // Skip the network round-trip entirely for a pinned package — the guard
    // below never renders anything for it, so fetching presets it can never
    // offer would be pure waste (and, in tests, a post-render `setState` on
    // an already-decided-null component). Rules-of-hooks still requires this
    // effect to run unconditionally on every render; only the WORK inside it
    // is skipped.
    if (entry.pkg.designBinding.rebinding !== 'allowed') return
    let cancelled = false
    getStudioUserId().then((userId) => {
      if (cancelled || !userId) return
      fetchDesignSystemPresets(userId).then((result) => {
        if (cancelled) return
        setPresets(result.presets)
        if (result.error) setPresetsError(copy.designRebindPresetLoadError)
      })
    })
    return () => {
      cancelled = true
    }
  }, [entry])

  // Belt-and-braces — see this file's header comment. TemplateContentPanel
  // already gates mounting on this same condition.
  if (entry.pkg.designBinding.rebinding !== 'allowed') return null

  async function handleApply() {
    const preset = presets.find((p) => p.id === selectedId)
    if (!preset) return

    setApplyStatus('loading')
    setApplyError(null)
    try {
      const result = await applyDesignRebind(entry, preset)
      if (result.ok) {
        toast.success(copy.designRebindApplySuccess)
      } else {
        setApplyError(result.error ?? copy.designRebindApplyFailed)
        toast.error(copy.designRebindApplyFailed)
      }
    } catch {
      // applyDesignRebind is expected to resolve (never reject) — this
      // guards against anything unexpected reaching here anyway, mirroring
      // TemplateContentPanel's own handleApply catch.
      setApplyError(copy.designRebindApplyFailed)
      toast.error(copy.designRebindApplyFailed)
    } finally {
      setApplyStatus('idle')
    }
  }

  return (
    <div className="border-b border-border bg-muted/30 p-3" data-testid="design-rebind-section">
      <div className="mb-1 text-xs font-semibold text-muted-foreground">{copy.designRebindTitle}</div>
      {presetsError && (
        <div className="mb-1 text-[11px] text-destructive" data-testid="design-rebind-presets-error">
          {presetsError}
        </div>
      )}
      <select
        data-testid="design-rebind-preset-select"
        className="w-full rounded border bg-background p-2 text-sm"
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
      >
        <option value="">{copy.designRebindPresetPlaceholder}</option>
        {presets.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {preset.name}
          </option>
        ))}
      </select>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          data-testid="design-rebind-apply"
          className="rounded border px-3 py-1 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleApply}
          disabled={!selectedId || applyStatus === 'loading'}
        >
          {applyStatus === 'loading' ? copy.designRebindApplying : copy.designRebindApply}
        </button>
      </div>
      {applyError && (
        <div className="mt-1 text-[11px] text-destructive" data-testid="design-rebind-apply-error">
          {applyError}
        </div>
      )}
    </div>
  )
}
